import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Card, Row, Col, Tag, Spin, message, Input, Tabs } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import customerApi from '../services/customerApi';
import { Business } from '../services/businesses';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

export const CustomerBusiness: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [businessSearchText, setBusinessSearchText] = useState<string>('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await customerApi.get('/customer/businesses');
      if (response.data.success) {
        setBusinesses(response.data.data || []);
      }
    } catch (error: any) {
      console.error('Error fetching data:', error);
      message.error(error.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getTypeColor = (type?: string) => {
    switch (type) {
      case 'potential':
        return 'orange';
      case 'active':
        return 'green';
      case 'inactive':
        return 'red';
      default:
        return 'default';
    }
  };

  const getTypeLabel = (type?: string) => {
    switch (type) {
      case 'potential':
        return 'Potential';
      case 'active':
        return 'Active';
      case 'inactive':
        return 'Inactive';
      default:
        return 'Existing';
    }
  };

  const getEntityTypeLabel = (entity?: string) => {
    if (!entity) return '-';
    const entityMap: Record<string, string> = {
      'Sdn Bhd': 'Sdn Bhd',
      'Bhd': 'Bhd',
      'Sdn': 'Sdn',
      'Enterprise': 'Enterprise',
      'Partnership': 'Partnership',
      'Sole Proprietorship': 'Sole Proprietorship',
    };
    return entityMap[entity] || entity;
  };

  const entityTypes = [
    'Sole Proprietorship',
    'Partnership',
    'Sdn Bhd',
    'LLP',
  ];

  const entityTypeMap: Record<string, string[]> = {
    'Sole Proprietorship': ['sole proprietorship'],
    'Partnership': ['partnership'],
    'Sdn Bhd': ['sdn bhd', 'private limited company'],
    'LLP': ['llp', 'limited liability partnership'],
  };

  // Filter businesses by search text
  const filteredBusinesses = useMemo(() => {
    let filtered = businesses;

    // Filter by search text
    if (businessSearchText) {
      const searchLower = businessSearchText.toLowerCase();
      filtered = filtered.filter((business) => {
        return (
          business.name?.toLowerCase().includes(searchLower) ||
          business.registration_no?.toLowerCase().includes(searchLower) ||
          business.entity?.toLowerCase().includes(searchLower)
        );
      });
    }
    
    return filtered;
  }, [businesses, businessSearchText]);

  const filteredBusinessesByEntity = useMemo(() => {
    return filteredBusinesses.reduce((acc, business) => {
      const entity = business.entity || 'Unknown';
      if (!acc[entity]) {
        acc[entity] = [];
      }
      acc[entity].push(business);
      return acc;
    }, {} as Record<string, Business[]>);
  }, [filteredBusinesses]);

  const getFilteredBusinessesForEntity = (entityType: string) => {
    const variations = entityTypeMap[entityType] || [];
    const allBusinesses: Business[] = [];

    variations.forEach((variation) => {
      Object.keys(filteredBusinessesByEntity).forEach((key) => {
        if (key.toLowerCase() === variation.toLowerCase()) {
          allBusinesses.push(...filteredBusinessesByEntity[key]);
        }
      });
    });

    return allBusinesses;
  };

  const renderInfoRow = (label: string, value: React.ReactNode) => (
    <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
      <td style={{ padding: '8px 12px', width: 160, backgroundColor: '#fafafa' }}>
        <strong>{label}</strong>
      </td>
      <td style={{ padding: '8px 12px' }}>{value}</td>
    </tr>
  );

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Input
          placeholder="Search Business"
          prefix={<SearchOutlined />}
          value={businessSearchText}
          onChange={(e) => setBusinessSearchText(e.target.value)}
          allowClear
          style={{ maxWidth: 300 }}
        />
      </div>
      <Tabs defaultActiveKey="Sole Proprietorship">
        {entityTypes.map((entityType) => {
          const entityBusinesses = getFilteredBusinessesForEntity(entityType);
          return (
            <TabPane tab={entityType} key={entityType}>
              <Row gutter={[16, 16]}>
                {entityBusinesses.map((business) => {
                  const status = business.type || 'existing';
                  const isActive = status === 'active' || status === 'existing';
                  const headerBgColor = isActive ? '#f6ffed' : '#fff1f0';
                  const titleColor = isActive ? '#52c41a' : '#ff4d4f';
                  const tagBgColor = isActive ? '#f6ffed' : '#fff1f0';
                  const tagBorderColor = isActive ? '#b7eb8f' : '#ffccc7';
                  const tagTextColor = isActive ? '#52c41a' : '#ff4d4f';
                  const dotColor = isActive ? '#52c41a' : '#ff4d4f';
                  const director = business.directors?.[0];
                  const member = business.members?.[0];

                  return (
                    <Col key={business.id} xs={24} sm={12} md={12} lg={12}>
                      <Card
                        style={{
                          height: '100%',
                          borderRadius: 8,
                          border: '1px solid #d9d9d9',
                          padding: 0,
                          overflow: 'hidden',
                          cursor: 'pointer',
                        }}
                        bodyStyle={{ padding: 0 }}
                        onClick={() => navigate(`/customer/businesses/${business.id}`)}
                      >
                        <div
                          style={{
                            backgroundColor: headerBgColor,
                            padding: '12px 16px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Title level={5} style={{ margin: 0, color: titleColor, fontWeight: 'bold' }}>
                            {business.name || '-'}
                          </Title>
                          <Tag
                            style={{
                              borderRadius: 16,
                              padding: '4px 12px',
                              fontSize: '12px',
                              backgroundColor: tagBgColor,
                              border: `1px solid ${tagBorderColor}`,
                              color: tagTextColor,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              margin: 0,
                            }}
                          >
                            <span
                              style={{
                                width: 4,
                                height: 4,
                                borderRadius: '50%',
                                backgroundColor: dotColor,
                                display: 'inline-block',
                              }}
                            />
                            {getTypeLabel(status)}
                          </Tag>
                        </div>
                        <div style={{ backgroundColor: '#fff' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <tbody>
                              {renderInfoRow('Registration No.', business.registration_no || '-')}
                              {renderInfoRow('Entity', getEntityTypeLabel(business.entity))}
                              {renderInfoRow('Position', director?.position || '-')}
                              {renderInfoRow('Share', member?.share ? `${member.share}%` : '-')}
                            </tbody>
                          </table>
                        </div>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
              {entityBusinesses.length === 0 && (
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                  No businesses found
                </div>
              )}
            </TabPane>
          );
        })}
      </Tabs>
      
      {filteredBusinesses.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          {businessSearchText ? 'No businesses found matching your search' : 'No businesses found'}
        </div>
      )}
    </div>
  );
};
